import { Request, Response } from 'express';
import { injectable, inject } from 'inversify';
import { ProjectApplicationService } from '../../../../application/services/ProjectApplicationService';
import { CreateProjectRequestSchema, UpdateProjectRequestSchema, GetProjectsQuerySchema } from '../dto/ProjectRequestDTOs';
import { validateRequest, validateProjectParams, createErrorResponse } from '../middleware/ValidationMiddleware';
import { 
  createSuccessResponse, 
  createErrorResponse as createStandardErrorResponse, 
  BUSINESS_ACTIONS, 
  DOMAIN_NAMES 
} from '@swoft/core';

/**
 * Project Controller
 * 
 * RESTful API controller for project operations.
 * Binds ONLY to ProjectApplicationService - no direct domain access.
 * 
 * Enforces Clean Architecture by maintaining strict API boundaries.
 */
@injectable()
export class ProjectController {
    constructor(
        @inject('ProjectApplicationService')
        private readonly projectApplicationService: ProjectApplicationService
    ) {}

    /**
     * Create a new project
     * POST /projects
     */
    async createProject(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = CreateProjectRequestSchema.parse(req.body);
            
            const result = await this.projectApplicationService.createProject(validatedRequest);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.CREATE_PROJECT,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(201).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PROJECT_CREATE_FAILED',
                'Failed to create project',
                BUSINESS_ACTIONS.DDD.CREATE_PROJECT,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get all projects with optional filtering
     * GET /projects
     */
    async getProjects(req: Request, res: Response): Promise<void> {
        try {
            // Validate query parameters
            const validatedQuery = GetProjectsQuerySchema.parse(req.query);
            
            const result = await this.projectApplicationService.getProjects(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    projects: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_PROJECTS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PROJECT_LIST_FAILED',
                'Failed to retrieve projects',
                BUSINESS_ACTIONS.DDD.LIST_PROJECTS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get a specific project by ID
     * GET /projects/:projectId
     */
    async getProject(req: Request, res: Response): Promise<void> {
        try {
            const { projectId } = req.params;
            
            const result = await this.projectApplicationService.getProject(projectId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.GET_PROJECT,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'PROJECT_GET_FAILED',
                'Failed to retrieve project',
                BUSINESS_ACTIONS.DDD.GET_PROJECT,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get project dashboard with metrics and insights
     * GET /projects/:projectId/dashboard
     */
    async getProjectDashboard(req: Request, res: Response): Promise<void> {
        try {
            const { projectId } = req.params;
            
            const result = await this.projectApplicationService.getProjectDashboard(projectId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.GET_PROJECT_DASHBOARD,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 500;
            const errorResponse = createStandardErrorResponse(
                'PROJECT_DASHBOARD_FAILED',
                'Failed to retrieve project dashboard',
                BUSINESS_ACTIONS.DDD.GET_PROJECT_DASHBOARD,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get middleware for project parameter validation
     */
    static getValidationMiddleware(): any {
        return {
            validateProjectParams,
            validateCreateProject: validateRequest({ body: CreateProjectRequestSchema }),
            validateUpdateProject: validateRequest({ body: UpdateProjectRequestSchema }),
            validateGetProjectsQuery: validateRequest({ query: GetProjectsQuerySchema })
        };
    }
}